import React, { useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CheckCircle2, Link2 } from 'lucide-react';
import { bindLscPaidSubscriptionToLeague, LscApiError } from '../api';
import { formatBindLeagueError } from '../entitlementBillingErrors';
import {
  formatPaidLinkedSuccessMessage,
  LSC_PAID_UNBOUND_MULTI_LEAGUE_MESSAGE,
  LSC_PAID_UNBOUND_RECOVERY_MESSAGE,
  LSC_PAID_UNBOUND_RECOVERY_TITLE,
} from '../billingCopy';
import { lscPath } from '../routing';
import { leagueSubscriptionSettingsHref } from '../paidUnboundRecovery';
import type { PaidUnboundRecoveryState } from '../paidUnboundRecovery';

interface PaidUnboundRecoveryBannerProps {
  recovery: PaidUnboundRecoveryState;
  settingsLeagueId?: string;
  onBound?: () => void;
}

function settingsHrefForLeague(leagueId: string): string {
  return lscPath(leagueSubscriptionSettingsHref(leagueId));
}

const PaidUnboundRecoveryBanner: React.FC<PaidUnboundRecoveryBannerProps> = ({
  recovery,
  settingsLeagueId,
  onBound,
}) => {
  const [bindLoading, setBindLoading] = useState(false);
  const [bindError, setBindError] = useState<string | null>(null);

  if (recovery.mode === 'hidden' || (!recovery.show && recovery.mode !== 'linked_success')) {
    return null;
  }

  if (recovery.mode === 'linked_success' && recovery.linkedLeagueName) {
    const leagueId = recovery.linkedLeagueId || settingsLeagueId;
    return (
      <div
        className="rounded-lg border border-emerald-700/60 bg-emerald-950/40 px-4 py-3 space-y-2"
        data-testid="lsc-paid-linked-success-banner"
        role="status"
      >
        <div className="flex items-start gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-300 shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm text-emerald-100">
            {formatPaidLinkedSuccessMessage(recovery.linkedLeagueName)}
          </p>
        </div>
        {leagueId ? (
          <Link
            to={settingsHrefForLeague(leagueId)}
            className="btn-secondary inline-flex text-sm"
            data-testid="lsc-paid-linked-settings-link"
          >
            View league settings
          </Link>
        ) : null}
      </div>
    );
  }

  const linkLeagueId =
    settingsLeagueId || recovery.settingsLeagueId || recovery.quickBindLeagueId;
  const settingsHref = linkLeagueId
    ? settingsHrefForLeague(linkLeagueId)
    : null;
  const ownedLeagues = recovery.ownedLeaguesForLink || [];

  const handleQuickBind = async () => {
    const leagueId = recovery.quickBindLeagueId;
    if (!leagueId) return;
    setBindLoading(true);
    setBindError(null);
    try {
      const result = await bindLscPaidSubscriptionToLeague(leagueId);
      if (result.success) {
        onBound?.();
        return;
      }
      setBindError('Could not link subscription to this league. Try League Settings or contact support.');
    } catch (err) {
      setBindError(formatBindLeagueError(err instanceof LscApiError ? err : err));
    } finally {
      setBindLoading(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-amber-700/60 bg-amber-950/40 px-4 py-3 space-y-3"
      data-testid="lsc-paid-unbound-recovery-banner"
      role="status"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" aria-hidden />
        <div className="space-y-1 min-w-0">
          <p className="text-sm font-medium text-amber-100">{LSC_PAID_UNBOUND_RECOVERY_TITLE}</p>
          <p className="text-sm text-amber-100/90">
            {recovery.canQuickBind
              ? LSC_PAID_UNBOUND_RECOVERY_MESSAGE
              : LSC_PAID_UNBOUND_MULTI_LEAGUE_MESSAGE}
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row flex-wrap gap-2">
        <Link to={lscPath('/leagues/new')} className="btn-primary text-sm" data-testid="paid-unbound-create-league">
          Start a league
        </Link>
        {recovery.canQuickBind ? (
          <>
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-2 text-sm"
              onClick={() => void handleQuickBind()}
              disabled={bindLoading}
              data-testid="lsc-paid-unbound-quick-bind"
            >
              <Link2 className="h-4 w-4" aria-hidden />
              {bindLoading ? 'Linking subscription…' : 'Link subscription to league'}
            </button>
            {settingsHref ? (
              <Link
                to={settingsHref}
                className="btn-secondary text-sm"
                data-testid="lsc-paid-unbound-settings-link"
              >
                Open League Settings
              </Link>
            ) : null}
          </>
        ) : ownedLeagues.length > 1 ? (
          ownedLeagues.map((league) => (
            <Link
              key={league.id}
              to={settingsHrefForLeague(league.id)}
              className="btn-secondary text-sm"
              data-testid={`lsc-paid-unbound-settings-link-${league.id}`}
            >
              Link in {league.name}
            </Link>
          ))
        ) : settingsHref ? (
          <Link
            to={settingsHref}
            className="btn-secondary text-sm"
            data-testid="lsc-paid-unbound-settings-link"
          >
            Link subscription in League Settings
          </Link>
        ) : null}
      </div>
      {bindError && (
        <p className="text-sm text-red-300" role="alert">
          {bindError}
        </p>
      )}
    </div>
  );
};

export default PaidUnboundRecoveryBanner;
